import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowUpRight } from "lucide-react";
import SEO from "../components/SEO";
import ProgramCard from "../components/ProgramCard";
import ProgramMasonryGrid from "../components/ProgramMasonryGrid";
import ProgramRecommender from "../components/ProgramRecommender";
import ReviewsSection from "../components/ReviewsSection";
import { fetchSitePrograms } from "../lib/api";

const AGE_FILTERS = [
  { label: "All ages", min: 0, max: 99 },
  { label: "4–6", min: 4, max: 6 },
  { label: "7–9", min: 7, max: 9 },
  { label: "10–12", min: 10, max: 12 },
  { label: "13–17", min: 13, max: 17 },
];

export default function ProgramsHubPage() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [ageFilter, setAgeFilter] = useState(AGE_FILTERS[0]);

  useEffect(() => {
    fetchSitePrograms()
      .then((arr) => setPrograms(arr || []))
      .catch(() => setPrograms([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(programs.map((p) => p.category).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [programs]);

  const filtered = useMemo(() => {
    return programs.filter((p) => {
      if (category !== "All" && p.category !== category) return false;
      if (ageFilter.label !== "All ages") {
        const pmin = p.age_min ?? 4;
        const pmax = p.age_max ?? 17;
        const overlap = ageFilter.min <= pmax && ageFilter.max >= pmin;
        if (!overlap) return false;
      }
      return true;
    });
  }, [programs, category, ageFilter]);

  const isFilterActive = category !== "All" || ageFilter.label !== "All ages";

  return (
    <>
      <SEO
        path="/programs"
        title="Programs for Kids in Richmond BC | STEM, Brick-Based Engineering, Robotics, Camps — Nexora"
        description="Explore Nexora programs in Richmond BC: STEM, Brick-Based Engineering, Robotics & Coding, Summer Camps, and Play-Based Behaviour Support. Find the right program for your child."
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#FFFCF7]" data-testid="programs-hub-hero">
        <div className="absolute -top-32 -right-24 w-[34rem] h-[34rem] rounded-full bg-sky-100/60 blur-[120px] opacity-70 pointer-events-none" />
        <div className="absolute -bottom-32 -left-24 w-[30rem] h-[30rem] rounded-full bg-[#FFE6CC]/60 blur-[120px] opacity-70 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 lg:pt-20 pb-12">
          <span className="pill bg-white border border-slate-200 text-slate-700 shadow-sm">
            <Sparkles className="h-3 w-3 text-[#FF8C42]" />
            All Nexora programs
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-extrabold text-slate-900 mt-5 max-w-3xl leading-[1.05]">
            Find the right{" "}
            <span className="text-[#0369A1]">program for your child.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
            Choose from hands-on STEM engineering, robotics and coding, camps,
            and play-based behaviour support.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/trial-class" className="btn-primary" data-testid="hub-trial-cta">
              Book a trial class
              <ArrowUpRight className="h-4 w-4" />
            </Link>
            <a href="#recommender" className="btn-soft" data-testid="hub-recommender-cta">
              Find the right program
            </a>
          </div>
        </div>
      </section>

      {/* Filters + Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="programs-hub-grid">
        <p
          className="max-w-3xl text-slate-600 leading-relaxed mb-8"
          data-testid="programs-hub-seo-intro"
        >
          Explore Nexora's children's programs in Richmond BC, including hands-on
          STEM engineering, brick-based building, robotics, summer camps, and play-based
          behaviour support. Featured programs first; full lineup below.
        </p>
        <div className="flex flex-col gap-5">
          <div>
            <p className="eyebrow text-slate-500">Filter by category</p>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="filter-category">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`pill border transition-colors ${
                    category === c
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                  }`}
                  data-testid={`filter-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="eyebrow text-slate-500">Filter by age</p>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="filter-age">
              {AGE_FILTERS.map((b) => (
                <button
                  key={b.label}
                  onClick={() => setAgeFilter(b)}
                  className={`pill border transition-colors ${
                    ageFilter.label === b.label
                      ? "bg-[#FF8C42] text-white border-[#FF8C42]"
                      : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                  }`}
                  data-testid={`filter-age-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-10">
          {loading ? (
            <p className="text-slate-500 text-sm">Loading programs…</p>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl bg-white border border-slate-200 p-8 text-center">
              <p className="font-display font-bold text-slate-900">
                No programs match those filters.
              </p>
              <p className="mt-2 text-sm text-slate-600">
                Try widening the age range or category.
              </p>
            </div>
          ) : isFilterActive ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p) => (
                <ProgramCard key={p.slug} program={p} />
              ))}
            </div>
          ) : (
            <ProgramMasonryGrid programs={filtered} />
          )}
        </div>
      </section>

      {/* Recommender */}
      <section
        id="recommender"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14"
      >
        <ProgramRecommender programs={programs} />
      </section>

      {/* Reviews */}
      <ReviewsSection />

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="rounded-[2.5rem] bg-slate-900 text-white p-10 sm:p-14 lg:p-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow text-slate-400">Ready when you are</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-extrabold mt-3 leading-[1.06]">
              Not sure which program to start with?
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed max-w-xl">
              Our coordinators will help you pick the right fit — and book a
              trial class so your child can experience it first.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-wrap gap-3">
            <Link
              to="/trial-class"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-slate-900 px-7 py-3.5 text-sm font-bold"
              data-testid="hub-final-trial-cta"
            >
              Book a trial class
              <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 px-7 py-3.5 text-sm font-bold text-white hover:bg-white/10"
            >
              Ask a question
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
